import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

let _ = t => t,
    _t;

import { Fragment, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIButton from 'UIComponents/button/UIButton';
import { H3 } from 'UIComponents/elements/headings';
import HR from 'UIComponents/elements/HR';
import UIClickable from 'UIComponents/button/UIClickable';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIImage from 'UIComponents/image/UIImage';
import { KOALA, BATTLESHIP, OLAF, EERIE, GYPSUM } from 'HubStyleTokens/colors';
import { AllProductLines, MARKETING } from 'self-service-api/constants/ProductLines';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
import { getAllFeaturesByAllProductLinesSelector } from '../../selectors/FeatureSelectors';
import FeatureTableRow from './FeatureTableRow';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import SuiteFeatureTableHeaderCell from './SuiteFeatureTableHeaderCell';
import breakpoint from '../../constants/Breakpoints';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { ProductToIconMap } from '../../constants/merchCards/ProductToIconMap';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import FeatureTableDropdownHeader from '../ebx633/FeatureTableDropdownHeader';
const TableContainer = styled.div.withConfig({
  displayName: "SuiteFeatureTable__TableContainer",
  componentId: "j4ftgm-0"
})(["width:100%;padding:16px 0 60px;padding:", ";margin:", ";", ""], props => props.isEbx633 && 0, props => props.isEbx633 && '100px 0 60px', breakpoint.tabletPortrait(_t || (_t = _`
    margin: 0 auto;
    display: block;
    overflow-x: hidden;
    width: 94%;
  `)));
const paddingLeft = css(["padding-left:42px;"]);
const rowHeaderStyles = css(["width:50%;text-align:left;", ""], paddingLeft);
const Table = styled.table.withConfig({
  displayName: "SuiteFeatureTable__Table",
  componentId: "j4ftgm-1"
})(["width:100%;border-spacing:", ";thead tr th{padding-bottom:24px;vertical-align:bottom;}thead tr:nth-child(1) th{", "}thead,tbody tr{display:table;width:100%;table-layout:fixed;}tbody tr{min-height:", ";}"], props => props.isEbx633 && 0, rowHeaderStyles, props => props.isEbx633 && '70px');
const ExpandableSectionLabel = styled.tbody.withConfig({
  displayName: "SuiteFeatureTable__ExpandableSectionLabel",
  componentId: "j4ftgm-2"
})(["background-color:", ";box-shadow:", ";tr td:nth-child(1) div > div{width:100%;text-align:left;padding-top:8px;padding-bottom:8px;", " display:flex;align-items:flex-end;}"], props => props.isEbx633 ? `${GYPSUM}` : `${KOALA}`, props => !props.isEbx633 && `0 0 0 1px ${BATTLESHIP}`, paddingLeft);
const ExpandableTableContent = styled.tbody.withConfig({
  displayName: "SuiteFeatureTable__ExpandableTableContent",
  componentId: "j4ftgm-3"
})(["position:relative;overflow:hidden;display:block;max-height:", ";transition:max-height 0.2s linear;&::after{visibility:", ";opacity:", ";position:absolute;bottom:0;height:100%;width:100%;content:'';background:linear-gradient(to bottom,transparent 70%,", ");pointer-events:none;}& tr td{padding:16px 0;text-align:center;border:", ";}& tr th[scope='row']{", " border:", ";}"], props => props.isSectionExpanded ? props.isFeatureTableExpanded ? '100%' : '350px' : '0', props => props.isFeatureTableExpanded ? 'hidden' : 'visible', props => props.isFeatureTableExpanded ? 0 : 1, OLAF, props => props.isEbx633 && `1px solid ${KOALA}`, rowHeaderStyles, props => props.isEbx633 && `1px solid ${KOALA}`);
const ExpandTableButtonContainer = styled.tbody.withConfig({
  displayName: "SuiteFeatureTable__ExpandTableButtonContainer",
  componentId: "j4ftgm-4"
})(["& tr td{padding-bottom:24px;", "}"], paddingLeft);
const initialSectionState = AllProductLines.reduce((agg, curr) => {
  // Default on page load, only Marketing section is expanded
  agg[curr] = curr === MARKETING;
  return agg;
}, {});
const ebx633InitialSectionState = AllProductLines.reduce((agg, curr) => {
  agg[curr] = true;
  return agg;
}, {});
const initialFeatureTableState = AllProductLines.reduce((agg, curr) => {
  agg[curr] = false;
  return agg;
}, {});

const SuiteFeatureTable = ({
  productsByProductLine,
  screen,
  tab,
  isEbx633
}) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(initialSectionState);
  const [isFeatureTableExpanded, setIsFeatureTableExpanded] = useState(initialFeatureTableState);
  const features = useSelector(getAllFeaturesByAllProductLinesSelector);
  const featureTableRef = useRef(null);
  useEffect(() => {
    if (isEbx633) {
      setIsSectionExpanded(ebx633InitialSectionState);
    }
  }, [isEbx633]);

  const handleExpandAllClick = () => {
    getUsageTracker().track('featureTableInteraction', {
      action: 'clicked-expand-all-sections',
      screen,
      tab
    });
    setIsSectionExpanded(prev => {
      return Object.keys(prev).reduce((agg, curr) => {
        agg[curr] = true;
        return agg;
      }, {});
    });
  };

  const handleCollapseAllClick = () => {
    getUsageTracker().track('featureTableInteraction', {
      action: 'clicked-collapse-all-sections',
      screen,
      tab
    });
    setIsSectionExpanded(prev => {
      return Object.keys(prev).reduce((agg, curr) => {
        agg[curr] = false;
        return agg;
      }, {});
    });
  };

  const handleExpandSectionChange = productLine => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${isSectionExpanded[productLine] ? 'collapse' : 'expand'}-section`,
      productLine,
      products: productsByProductLine[productLine],
      screen,
      tab
    });
    setIsSectionExpanded(prev => {
      return Object.assign({}, prev, {
        [productLine]: !prev[productLine]
      });
    });
  };

  const handleExpandFeatureTableChange = productLine => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${isFeatureTableExpanded[productLine] ? 'collapse' : 'expand'}-table`,
      productLine,
      products: productsByProductLine[productLine],
      screen,
      tab
    });
    setIsFeatureTableExpanded(prev => {
      return Object.assign({}, prev, {
        [productLine]: !prev[productLine]
      });
    });
  };

  if (!Object.keys(features).length) return null;
  const sortedProductLines = sortProductLines(Object.keys(features));
  const levels = new Set();
  Object.values(productsByProductLine).forEach(products => {
    products.forEach(product => {
      const level = getProductLevelFromApiNameString(product);
      levels.add(level);
    });
  }); //ebx633

  let productLineTitle = 'title';
  let expandLabel = 'expand';
  let collapseLabel = 'collapse';

  if (isEbx633) {
    productLineTitle = 'ebx633Title';
    expandLabel = 'ebx633Expand';
    collapseLabel = 'ebx633Collapse';
  }

  return /*#__PURE__*/_jsxs(TableContainer, {
    ref: featureTableRef,
    isEbx633: isEbx633,
    children: [isEbx633 && /*#__PURE__*/_jsx(FeatureTableDropdownHeader, {
      levels: [...levels],
      tableRef: featureTableRef
    }), /*#__PURE__*/_jsx("div", {
      className: "text-center m-y-5",
      children: /*#__PURE__*/_jsx(H3, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "suiteFeatureTable.title"
        })
      })
    }), !isEbx633 && /*#__PURE__*/_jsx(HR, {}), /*#__PURE__*/_jsxs(Table, {
      isEbx633: isEbx633,
      children: [!isEbx633 && /*#__PURE__*/_jsxs("thead", {
        children: [/*#__PURE__*/_jsx("tr", {
          children: /*#__PURE__*/_jsx("th", {
            rowSpan: 2,
            children: Object.values(isSectionExpanded).every(status => status === true) ? /*#__PURE__*/_jsx(UIButton, {
              use: "link",
              onClick: handleCollapseAllClick,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "suiteFeatureTable.collapseAll"
              })
            }) : /*#__PURE__*/_jsx(UIButton, {
              use: "link",
              onClick: handleExpandAllClick,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "suiteFeatureTable.expandAll"
              })
            })
          })
        }), /*#__PURE__*/_jsx("tr", {
          children: [...levels].map(level => {
            return /*#__PURE__*/_jsx(SuiteFeatureTableHeaderCell, {
              productLevel: level
            }, level);
          })
        })]
      }), sortedProductLines.map(productLine => {
        const localFeatures = features[productLine];
        const highlightedProducts = productsByProductLine[productLine];
        if (!highlightedProducts.length) return null;
        return /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(ExpandableSectionLabel, {
            isEbx633: isEbx633,
            children: /*#__PURE__*/_jsx("tr", {
              children: /*#__PURE__*/_jsx("td", {
                colSpan: levels.size + 1,
                children: /*#__PURE__*/_jsx(UIClickable, {
                  onClick: () => handleExpandSectionChange(productLine),
                  "aria-expanded": isSectionExpanded[productLine] ? 'true' : 'false',
                  "aria-controls": `suite-feature-table-${productLine}-section`,
                  children: /*#__PURE__*/_jsxs("div", {
                    className: `${isEbx633} && display-flex align-center`,
                    children: [isEbx633 ? /*#__PURE__*/_jsx(UIImage, {
                      src: ProductToIconMap[productLine][STARTER],
                      width: "24",
                      className: "m-right-3"
                    }) : /*#__PURE__*/_jsx(UIIcon, {
                      name: isSectionExpanded[productLine] ? 'down' : 'right',
                      size: "xxs",
                      color: EERIE,
                      className: "p-right-2"
                    }), /*#__PURE__*/_jsx("strong", {
                      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                        message: `suiteFeatureTable.section.${productLineTitle}`,
                        options: {
                          product: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: `productLine.${productLine}`
                          })
                        }
                      })
                    })]
                  })
                })
              })
            })
          }), /*#__PURE__*/_jsx(ExpandableTableContent, {
            id: `suite-feature-table-${productLine}-section`,
            isSectionExpanded: isSectionExpanded[productLine],
            isFeatureTableExpanded: isFeatureTableExpanded[productLine],
            isEbx633: isEbx633,
            children: localFeatures.map(feature => {
              return /*#__PURE__*/_jsx(FeatureTableRow, {
                highlightedProducts: highlightedProducts,
                feature: feature,
                productLine: productLine
              }, feature.featureId);
            })
          }), isSectionExpanded[productLine] && /*#__PURE__*/_jsx(ExpandTableButtonContainer, {
            children: /*#__PURE__*/_jsx("tr", {
              children: /*#__PURE__*/_jsx("td", {
                colSpan: levels.size + 1,
                className: `${isEbx633 && 'display-flex align-center'}`,
                children: /*#__PURE__*/_jsxs(UIButton, {
                  use: "transparent",
                  onClick: () => {
                    handleExpandFeatureTableChange(productLine);
                  },
                  "aria-expanded": isSectionExpanded[productLine] ? 'true' : 'false',
                  className: `p-x-0 ${isEbx633 && 'p-left-8'}`,
                  children: [/*#__PURE__*/_jsx(FormattedMessage, {
                    message: isFeatureTableExpanded[productLine] ? `suiteFeatureTable.${collapseLabel}` : `suiteFeatureTable.${expandLabel}`
                  }), isEbx633 && /*#__PURE__*/_jsx(UIIcon, {
                    name: isFeatureTableExpanded[productLine] ? 'upCarat' : 'downCarat',
                    color: EERIE
                  })]
                })
              })
            })
          })]
        }, productLine);
      })]
    })]
  });
};

export default SuiteFeatureTable;